import type Quill from "quill";
import type { TextWidgetV2 } from "../../textV2";
import { getSafeBounds } from "../../utilsV2";
import type { SceneRect, VirtualAnchor } from "../types";
import { localRectToSceneQuad, quadToClientDOMRect } from "./canvasGeometry";

function getSafeQuillBounds(quill: Quill, index: number) {
    const len = Math.max(0, quill.getLength() - 1);

    const candidates = [
        Math.max(0, Math.min(index, len)),
        Math.max(0, Math.min(index - 1, len)),
        0,
    ];

    for (const candidate of candidates) {
        const bounds = getSafeBounds(quill, candidate, 0);
        if (bounds) {
            return bounds;
        }
    }

    return null;
}

export function createQuillRangeAnchor(
    key: string,
    quill: Quill,
    index: number,
    length: number,
): VirtualAnchor {
    return {
        key,
        getBoundingClientRect(): DOMRect {
            const rootRect = (quill.root as HTMLElement).getBoundingClientRect();

            const start = getSafeQuillBounds(quill, index);
            const end = getSafeQuillBounds(quill, index + Math.max(0, length - 1));

            if (!start || !end) {
                return DOMRect.fromRect({
                    x: rootRect.left,
                    y: rootRect.top,
                    width: 0,
                    height: 0,
                });
            }

            const left = Math.min(start.left, end.left);
            const top = Math.min(start.top, end.top);
            const right = Math.max(start.left + start.width, end.left + end.width);
            const bottom = Math.max(start.top + start.height, end.top + end.height);

            return DOMRect.fromRect({
                x: rootRect.left + left,
                y: rootRect.top + top,
                width: Math.max(0, right - left),
                height: Math.max(0, bottom - top),
            });
        },
    };
}

export function createCanvasRunAnchor(
    key: string,
    widget: TextWidgetV2,
    localRect: SceneRect,
): VirtualAnchor {
    return {
        key,
        getBoundingClientRect(): DOMRect {
            const quad = localRectToSceneQuad(widget, localRect);
            return quadToClientDOMRect(quad);
        },
    };
}
